import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "./ui/input";

/**
 * Lightweight searchable dropdown.
 *
 * - Typing in the input filters the option list (case-insensitive substring, prefers `startsWith`
 *   matches so that "otp" ranks "otp_login" above "notification_otp_reset").
 * - Clicking an option or pressing Enter on the highlighted one calls `onChange(value)`.
 * - When the input is focused, the full option list drops down; the input's value is what's
 *   committed to state via `onChange`, so a user can also type a name that isn't in the list
 *   (useful for a template the admin knows exists but hasn't downloaded yet).
 * - Options are `{name, category?, language?}`. `name` is used as the value and the visible label.
 *
 * No portals, no external deps — just enough to give the "type initials to filter" experience
 * the spec asked for.
 */
export function SearchableCombobox({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  emptyLabel,
  testId,
}) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  // Sync incoming `value` prop -> internal query text
  useEffect(() => {
    if (value !== undefined && value !== null) setQuery(value);
    else setQuery("");
  }, [value]);

  // Click-outside to close
  useEffect(() => {
    function handler(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return options;
    const starts = [];
    const contains = [];
    for (const opt of options) {
      const name = (opt.name || "").toLowerCase();
      if (!name) continue;
      if (name.startsWith(q)) starts.push(opt);
      else if (name.includes(q)) contains.push(opt);
    }
    return [...starts, ...contains];
  }, [query, options]);

  function commit(name) {
    setQuery(name);
    onChange(name);
    setOpen(false);
    setActiveIdx(-1);
  }

  function handleKeyDown(e) {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && filtered[activeIdx]) {
        e.preventDefault();
        commit(filtered[activeIdx].name);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
          setActiveIdx(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        data-testid={testId}
      />
      {open && (
        <div
          ref={listRef}
          className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-card shadow-xl"
          data-testid={testId ? `${testId}-dropdown` : undefined}
        >
          {options.length === 0 ? (
            <div className="p-3 text-xs text-muted" data-testid={testId ? `${testId}-empty` : undefined}>
              {emptyLabel || "No options available."}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-3 text-xs text-muted">No template matches "{query}".</div>
          ) : (
            filtered.map((opt, idx) => (
              <button
                key={opt.name}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(opt.name)}
                onMouseEnter={() => setActiveIdx(idx)}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                  idx === activeIdx ? "bg-elevated text-gold" : "hover:bg-elevated/70 text-text"
                }`}
                data-testid={testId ? `${testId}-option-${opt.name}` : undefined}
              >
                <span className="truncate">{opt.name}</span>
                <span className="flex shrink-0 items-center gap-1 text-[10px] uppercase tracking-widest text-muted">
                  {opt.category && <span>{opt.category}</span>}
                  {opt.language && <span className="text-gold/80">{opt.language}</span>}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
